// src/components/ProductRow.jsx
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import ProductCard from "./ProductCard";

/**
 * ProductRow est "variant-aware" :
 * - Il accepte un onAdd(p, qty, meta) et le propage tel quel vers le parent.
 * - Pas de logique forcée de variantKey "default" ici.
 * - Compatible avec ProductCard + VariantPickerModal.
 */
export default function ProductRow({
  title,
  items = [],
  onAdd,
  seeAllHref,          // optionnel
  seeAllText = "Voir tout",
  subtitle,            // optionnel
}) {
  // Propage simplement l’événement vers le parent
  const handleAdd = (p, qty = 1, meta = {}) => onAdd?.(p, qty, meta);

  const hasItems = Array.isArray(items) && items.length > 0;

  // (optionnel) éviter key undefined + rendre stable
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  if (!hasItems) return null;

  return (
    <section className="pr-wrap mb-4" aria-label={title || "Produits"}>
      {/* Header */}
      {(title || subtitle || seeAllHref) && (
        <header className="pr-head d-flex align-items-end justify-content-between gap-2 mb-2">
          <div>
            {title ? <h2 className="pr-title m-0">{title}</h2> : null}
            {subtitle ? <div className="pr-sub text-muted">{subtitle}</div> : null}
          </div>

          {seeAllHref ? (
            <Link to={seeAllHref} className="pr-link btn btn-outline-dark btn-sm">
              {seeAllText} <span aria-hidden="true">→</span>
            </Link>
          ) : null}
        </header>
      )}

      {/* Grid */}
      <div className="row row-cols-2 row-cols-sm-3 row-cols-lg-4 row-cols-xl-4 g-3">
        {list.map((p, idx) => (
          <div className="col" key={p?.id || p?.slug || `p-${idx}`}>
            <ProductCard
              p={p}
              onAdd={handleAdd} // reçoit (p, qty, { variantKey, variant, variantLabel })
            />
          </div>
        ))}
      </div>

      {/* Styles locaux (léger + élégant, sans casser Bootstrap) */}
      <style>{`
        .pr-wrap { position: relative; }
        .pr-head { padding: 2px 0; }
        .pr-title {
          font-size: 1.05rem;
          font-weight: 800;
          letter-spacing: .2px;
        }
        .pr-sub { font-size: .9rem; margin-top: 2px; }
        .pr-link { border-radius: 999px; }
        @media (max-width: 576px){
          .pr-title { font-size: 1rem; }
          .pr-link { padding: .35rem .6rem; }
        }
      `}</style>
    </section>
  );
}
